import { Anchor, Avatar, Card, Center, Divider, Grid, Group, Image, rem, Space, Stack, Text } from "@mantine/core"
import { IconProps } from "@tabler/icons-react"
import { motion, useInView } from "framer-motion"

import React, { useRef } from "react"
import MySplitText from "../../Components/SplitText"
import { IPerson } from "../About/Page"
import { IBranch } from "../Branches/Props"
import { ISubsidiary } from "../Home/Props"

export function multiLineEntryWithIcon(title: string, subtitle: string, Icon: React.FC<IconProps>): React.ReactElement {
  return (
    <Group gap={rem(24)} justify="flex-start">
      <Icon size={32} stroke={1.5} />

      <Stack gap={rem(4)} align="flex-start">
        <Text size={rem(16)} fw={600}>
          {title}
        </Text>
        <Text size={rem(14)} opacity={0.75}>
          {subtitle}
        </Text>
      </Stack>
    </Group>
  )
}

export function multiLineEntry(title: string, subtitle: string): React.ReactElement {
  return (
    <Stack gap={rem(4)} align="flex-start">
      <Text size={rem(16)} fw={600}>
        {title}
      </Text>
      <Text size={rem(14)} opacity={0.75}>
        {subtitle}
      </Text>
    </Stack>
  )
}

export function mainHeader(): React.ReactElement {
  return (
    <Group gap={"md"} align="center" justify={"space-between"}>
      <Image src={"/logo.png"} alt="Dharohar Logo" w={rem(164)} />

      <Stack gap={"md"} align="end" justify="end" style={{ textAlign: "right" }}>
        <Text size={"96px"} fw={800} c={"#281c6b"} style={{ letterSpacing: rem(5) }}>
          {"Dharohar Group"}
        </Text>

        <MySplitText
          text="Rise Beyond Limits.."
          delay={100}
          duration={0.75}
          ease="power3.out"
          splitType="chars"
          from={{ opacity: 0, y: 40 }}
          to={{ opacity: 1, y: 0 }}
          threshold={0.1}
          rootMargin="-200px"
          textAlign="center"
        />
      </Stack>
    </Group>
  )
}

export function footer(): React.ReactElement {
  return (
    <Stack gap={rem(8)}>
      <Divider variant={"solid"} />
      <Space h={rem(8)} />
      <Stack justify="center" align="center" style={{ textAlign: "center" }}>
        <Text size={rem(14)} opacity={0.75}>
          © {new Date().getFullYear()} Dharohar Group. All rights reserved.
        </Text>
        <Group>
          <Anchor c="dimmed" href={"/privacy-policy"} onClick={(event) => event.preventDefault()} size={rem(12)}>
            Contact Us
          </Anchor>
          <Anchor c="dimmed" href={"/privacy-policy"} onClick={(event) => event.preventDefault()} size={rem(12)}>
            Privacy Policy
          </Anchor>
          <Anchor c="dimmed" href={"/privacy-policy"} onClick={(event) => event.preventDefault()} size={rem(12)}>
            Terms & Conditions
          </Anchor>
        </Group>
      </Stack>
    </Stack>
  )
}

export function primaryCard(index: number, image: string, title: string, content: string): React.ReactElement {
  return (
    <AnimatedWrapper index={index} y={50}>
      <Card
        withBorder
        p={0}
        shadow="sm"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Image src={image} alt="Product Image" w={"100%"} h={"164px"} />

        <Stack gap={rem(16)} p="md" style={{ flexGrow: 1 }}>
          <Text size={rem(20)} fw={600}>
            {title}
          </Text>
          <Text size={rem(16)} opacity={0.85} style={{ whiteSpace: "pre-line" }}>
            {content}
          </Text>
        </Stack>

        <Space h={rem(16)} />
      </Card>
    </AnimatedWrapper>
  )
}

export function subsidiaryCard(index: number, subsidiary: ISubsidiary): React.ReactElement {
  return (
    <AnimatedWrapper index={index} y={50}>
      <Card
        withBorder
        p={0}
        shadow="sm"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Image src={subsidiary.image} alt="Product Image" w={"100%"} h={"164px"} />

        <Stack gap={rem(16)} p="md" style={{ flexGrow: 1 }}>
          <Text size={rem(20)} fw={600}>
            {subsidiary.name}
          </Text>
          <Text size={rem(16)} opacity={0.85}>
            {subsidiary.description}
          </Text>
        </Stack>

        <Space h={rem(16)} />
      </Card>
    </AnimatedWrapper>
  )
}

export function branchCard(index: number, branch: IBranch): React.ReactElement {
  return (
    <AnimatedWrapper index={index} y={50}>
      <Card
        withBorder
        p={0}
        shadow="sm"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Image src={branch.image} alt="Product Image" w={"100%"} h={"120px"} />

        <Stack gap={rem(16)} p="md" style={{ flexGrow: 1 }}>
          <Text size={rem(20)} fw={600}>
            {branch.name}
          </Text>
          <Text size={rem(14)} fw={500}>
            {branch.location}
          </Text>
          <Text size={rem(16)} opacity={0.85}>
            {branch.description}
          </Text>
        </Stack>

        <Space h={rem(16)} />
      </Card>
    </AnimatedWrapper>
  )
}

export function founderCardWithPhoto(idx: number, person: IPerson): React.ReactElement {
  return (
    <motion.div
      key={idx}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.125 }}
    >
      <Card key={idx} radius={rem(8)} withBorder>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Center>
              <Stack gap={0} align="center" justify="center" w={"100%"}>
                <Image
                  src={person.image}
                  alt={person.name}
                  radius="md"
                  mah={"480px"}
                  maw={"320px"}
                  style={{ textAlign: "center" }}
                />
                <Text fw={600} size="lg">
                  {person.name}
                </Text>
                <Text size="md" c="dimmed">
                  {person.role}
                </Text>
              </Stack>
            </Center>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 9 }}>
            <Text size="md" style={{ whiteSpace: "pre-line" }}>
              {person.message}
            </Text>
          </Grid.Col>

          <Stack gap={rem(2)} align="center" justify="center"></Stack>
        </Grid>
      </Card>
    </motion.div>
  )
}

export function personCard(index: number, person: IPerson): React.ReactElement {
  return (
    <AnimatedWrapper index={index}>
      <Card key={index} shadow="sm" withBorder style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Group>
          <Avatar src={person.image} alt={person.name} radius="xl" size="lg" name={person.name} color={"initials"} />
          <Stack gap={rem(2)}>
            <Text fw={600} size="lg">
              {person.name}
            </Text>
            <Text size="md" c="dimmed">
              {person.role}
            </Text>
          </Stack>
        </Group>

        {person.message && (
          <>
            <Divider my="xs" />
            <Text size="md" style={{ whiteSpace: "pre-line" }}>
              {person.message}
            </Text>
          </>
        )}
      </Card>
    </AnimatedWrapper>
  )
}

export function AnimatedWrapper(props: AnimatedWrapperProps): React.ReactElement {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  const transition = {
    duration: 0.5,
    delay: props.index * 0.125,
    ease: "easeOut"
  }

  return (
    <motion.div
      ref={ref}
      className="product"
      initial={{ opacity: 0, y: props.y ?? 15 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        opacity: transition,
        y: transition
      }}
      whileHover={{
        scale: 1.02,
        transition: { duration: 0.1 }
      }}
      whileTap={{ scale: 1 }}
    >
      {props.children}
    </motion.div>
  )
}

interface AnimatedWrapperProps {
  index: number
  children: React.ReactNode
  y?: number
}
